import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Pagination from '../common/Pagination';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  AUDIT_TRAIL_COLUMN_LABELS,
  AUDIT_TRAIL_COLUMNS_DEFAULT_VISIBILITY,
  type AuditTrailColumnKey,
} from '../../constants/acknowledgementAuditTrail.constants';
import type { AuditTrailListItem } from '../../types/acknowledgementAuditTrail.types';
import {
  formatAcknowledgedByCell,
  formatCriticalityBadge,
  resolveAuditTrailStudyLabel,
  resolveDisplayAckStatus,
} from '../../utils/acknowledgementAuditTrailUtils';

interface AcknowledgementAuditTrailTableProps {
  rows: AuditTrailListItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  loading?: boolean;
  error?: string | null;
  onPageChange: (page: number) => void;
  onRowSelect: (row: AuditTrailListItem) => void;
}

const TOGGLEABLE_COLUMNS = Object.keys(AUDIT_TRAIL_COLUMN_LABELS) as AuditTrailColumnKey[];

export default function AcknowledgementAuditTrailTable({
  rows,
  totalCount,
  currentPage,
  pageSize,
  loading = false,
  error = null,
  onPageChange,
  onRowSelect,
}: Readonly<AcknowledgementAuditTrailTableProps>) {
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(AUDIT_TRAIL_COLUMNS_DEFAULT_VISIBILITY);
  const columnsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeColumns = useMemo(
    () => TOGGLEABLE_COLUMNS.filter(columnKey => visibleColumns[columnKey]),
    [visibleColumns]
  );

  const columnCount = Math.max(activeColumns.length, 1);

  const bodyContent = useMemo(() => {
    if (loading) {
      return (
        <tr>
          <td
            colSpan={columnCount}
            className="aat-table-message"
          >
            Loading acknowledgement audit records…
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan={columnCount}
            className="aat-table-message aat-table-message--error"
          >
            <div role="alert">{error}</div>
          </td>
        </tr>
      );
    }

    if (!rows.length) {
      return (
        <tr>
          <td
            colSpan={columnCount}
            className="aat-table-message"
          >
            No acknowledgement audit records match the current filters.
          </td>
        </tr>
      );
    }

    return rows.map(row => {
      const criticality = formatCriticalityBadge(row.criticalityBand);
      const ackStatus = resolveDisplayAckStatus(row);
      const acknowledgedBy = formatAcknowledgedByCell(row);
      const escalationLabel =
        row.currentEscalationLevel && row.currentEscalationLevel > 0
          ? `L${row.currentEscalationLevel}`
          : '—';

      return (
        <tr
          key={row.id}
          className="aat-table-row"
          tabIndex={0}
          onClick={() => onRowSelect(row)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onRowSelect(row);
            }
          }}
        >
          {activeColumns.map(columnKey => {
            switch (columnKey) {
              case 'accession':
                return (
                  <td key={columnKey}>
                    <span className="aat-cell-text">{row.accessionNumber || '—'}</span>
                  </td>
                );
              case 'patient':
                return (
                  <td key={columnKey}>
                    <div className="aat-cell-stack">
                      <span className="aat-cell-primary">{row.patientName || '—'}</span>
                      {row.patientDicomId ? (
                        <span className="aat-cell-sub">{row.patientDicomId}</span>
                      ) : null}
                    </div>
                  </td>
                );
              case 'study':
                return (
                  <td key={columnKey}>
                    <span className="aat-cell-study">{resolveAuditTrailStudyLabel(row)}</span>
                  </td>
                );
              case 'criticality':
                return (
                  <td key={columnKey}>
                    <span className={criticality.className}>{criticality.label}</span>
                  </td>
                );
              case 'ackStatus':
                return (
                  <td key={columnKey}>
                    <span className={ackStatus.className}>{ackStatus.label}</span>
                  </td>
                );
              case 'acknowledgedBy':
                return (
                  <td key={columnKey}>
                    {acknowledgedBy.name ? (
                      <div className="aat-cell-stack">
                        <span className="aat-cell-primary">{acknowledgedBy.name}</span>
                        {acknowledgedBy.meta ? (
                          <span className="aat-cell-sub">{acknowledgedBy.meta}</span>
                        ) : null}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                );
              case 'escLevel':
                return (
                  <td key={columnKey}>
                    <span className="aat-cell-text">{escalationLabel}</span>
                  </td>
                );
              case 'events':
                return (
                  <td key={columnKey}>
                    <span className="aat-cell-text">{row.eventCount ?? '—'}</span>
                  </td>
                );
              default:
                return null;
            }
          })}
        </tr>
      );
    });
  }, [rows, loading, error, onRowSelect, activeColumns, columnCount]);

  return (
    <section className="crd-panel crd-alerts-panel aat-table-panel">
      <header className="crd-panel-header crd-alerts-table-header aat-table-header">
        <div className="crd-alerts-table-heading">
          <h2 className="crd-panel-title">Acknowledgement audit records</h2>
          <p className="crd-alerts-count">{totalCount} records</p>
        </div>
        <div className="crd-alerts-toolbar">
          <div
            className="crd-columns-wrap"
            ref={columnsRef}
          >
            <button
              type="button"
              className="crd-btn crd-btn--secondary crd-columns-button"
              onClick={() => setColumnsOpen(open => !open)}
            >
              Columns
              <ChevronDown
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            {columnsOpen ? (
              <div
                className="crd-columns-menu"
                role="menu"
              >
                {TOGGLEABLE_COLUMNS.map(columnKey => (
                  <label
                    key={columnKey}
                    className="crd-columns-option"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[columnKey]}
                      onChange={event =>
                        setVisibleColumns(current => ({
                          ...current,
                          [columnKey]: event.target.checked,
                        }))
                      }
                    />
                    <span>{AUDIT_TRAIL_COLUMN_LABELS[columnKey]}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="aat-table-wrap">
        <table className="aat-table">
          <thead>
            <tr>
              {activeColumns.map(columnKey => (
                <th key={columnKey}>{AUDIT_TRAIL_COLUMN_LABELS[columnKey]}</th>
              ))}
            </tr>
          </thead>
          <tbody>{bodyContent}</tbody>
        </table>
      </div>

      {!loading && !error && totalCount > pageSize ? (
        <Pagination
          currentPage={currentPage}
          totalItems={totalCount}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
        />
      ) : null}
    </section>
  );
}
